import { Component, Injector, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { AbstractComponent, Card, CardService, Channel, FirebaseService, Pager, PRIORITY_LABEL, SALES_ACTIVITY_TYPES, Task, TaskAttachments, TaskPriorityEnum, TasksService, TaskStatusEnum, TaskTypeEnum, UploadTypeEnum, User, UserService, taskTypeLabelKey } from 'lib-trend-core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { ScheduleMoment } from '../activity-dialogs/schedule-moment-field/schedule-moment-field.component';

@Component({
    selector: 'app-task-form',
    templateUrl: './task-form.component.html',
    styleUrl: './task-form.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskFormComponent extends AbstractComponent {
  private termOfSearch: Subject<string> = new Subject<string>();

  pager: Pager<Task> = new Pager<Task>({ perPage: 10 });
  listObservable: Observable<Pager<Task>>;

  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();

  public task: Task;
  todayTasks: Array<Task> = new Array<Task>();

  public listChannel!: Array<Channel>;
  public listUser: Array<User> = new Array<User>();

  /** The six priorities, all accepted by the API since E4 (RF-50). */
  priorities = Object.values(TaskPriorityEnum).map(v => ({ value: v, label: PRIORITY_LABEL[v] }));

  breadcrumb = [];

  public file: File;
  public fileLoaded = false;
  public bytes: string;
  public attachments: Array<TaskAttachments> = [];

  constructor(
    injector: Injector,
    private taskService: TasksService,
    private userService: UserService,
    private cardService: CardService,
    public firebaseService: FirebaseService,
  ) {
    super(injector);
    this.createForm();
  }

  /**
   * The seven sales types (E4 RF-01), plus the type the edited activity already carries when it
   * is an older one (RF-02): it stays selected and readable, never offered for a new activity.
   * Labelled by the one shared helper, like every other screen that shows a type.
   */
  get taskTypes(): Array<{ value: string; label: string }> {
    const current = this.task?.type;
    const types: Array<string> = [...SALES_ACTIVITY_TYPES];
    if (current && !types.includes(current)) types.push(current);
    return types.map((type) => ({ value: type, label: taskTypeLabelKey(type) }));
  }

  ngOnInit(): void {
    const taskId = this.getParam("id");
    this.isNew = !taskId;

    this.breadcrumb = [
      { href: '/task', label: this.t('task.form.breadcrumbTasks') },
      { label: this.isNew ? this.t('task.form.breadcrumbAddTask') : this.t('task.form.breadcrumbEditTask') },
    ];

    if (taskId) {
      this.taskService.getById(taskId).subscribe((task: Task) => {
        this.task = task;
        this.attachments = task?.attachments ?? [];

        const userId = task?.user ? String(task?.user._id ?? task?.user) : null;
        // `card` comes back POPULATED from the API (it is needed for display elsewhere),
        // but the select binds an id — same normalisation the assignee already gets.
        const cardId = task?.card ? String(task.card._id ?? task.card) : null;
        // The deadline is one control — date + time + "sem horário" (E4 RF-04, RF-05): the mark
        // comes from the activity, never from the time of day it holds.
        const moment: ScheduleMoment | null = task?.deadlineAt
          ? { dueAt: new Date(task.deadlineAt), untimed: task.untimed === true }
          : null;
        const formObject = { ...this.task, user: userId, card: cardId, moment };

        if (userId && !this.listUser?.some(u => String(u._id) === userId)) {
          this.listUser = [{ _id: userId, name: '' } as any, ...(this.listUser ?? [])];
        }

        this.formGroup.patchValue(formObject, { emitEvent: false });
      });
    }
    this.getListUser();
    this.getListCard();
  }

  back(): void {
    history.back();
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      name: [null, Validators.required],
      description: [null],
      type: [TaskTypeEnum.TASK, Validators.required],
      priority: [TaskPriorityEnum.MEDIUM, Validators.required],
      status: [TaskStatusEnum.PENDING, Validators.required],
      user: [null],
      // Optional link to an opportunity — deal activities are these same tasks (ADR-004).
      card: [null],
      // Optional: a to-do may have no scheduled moment at all.
      moment: [null],
      attachments: [[]],
    });

    this.formGroup.patchValue({ ...this.task }, { emitEvent: false });
  }

  /**
   * Deals the user may link a task to.
   *
   * Uses the deal picker endpoint, which returns only the deals this user is allowed to
   * see — so a task cannot be linked to an opportunity the user cannot open.
   */
  listCard: Array<Card> = [];

  private getListCard(): void {
    if (!this.getModuleCRM()) return;

    this.cardService
      .getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list: Array<Card>) => this.listCard = list ?? [],
        // A failed load leaves the field empty rather than blocking the form.
        error: () => this.listCard = [],
      });
  }

  private getListUser(): void {
    this.userService.getList().subscribe({
      next: (list: Array<User>) => {
        this.listUser = list;
      },
    })
  }

  onAttachmentsSelect(event: any): void {
    const target = event.target;
    const files: FileList = target.files;
    this.file = files[0];

    let reader = new FileReader();
    reader.onload = async (file) => {
      if (this.file) {
        this.loadingSpinnerSubject.next(true);
        const dateMilisecond = new Date().getTime();
        const filename: string = dateMilisecond.toString();

        this.firebaseService.uploadFile(this.file, filename, UploadTypeEnum.TASK).then((snapshot: { url: string }) => {
          this.loadingSpinnerSubject.next(false);
          const currentAttachments = this.formGroup.get('attachment')?.value ?? [];
          this.formGroup.patchValue({ attachment: [...currentAttachments, snapshot.url] });

          const newAttachment: TaskAttachments = {
            filename: this.file.name,
            contentType: this.file.type,
            size: this.file.size,
            url: snapshot.url,
          };

          this.attachments = [...this.attachments, newAttachment];
          this.fileLoaded = true;
        })
          .catch((error) => this.alertService.error(this.t('task.form.uploadError')));
      }
    };
    reader.readAsArrayBuffer(this.file);
  }

  removeAttachment(index: number): void {
    const current: string[] = this.formGroup.get('attachment')?.value ?? [];
    const next = current.filter((_, i) => i !== index);

    this.attachments.splice(index, 1);

    this.formGroup.patchValue({ attachment: next });
    this.formGroup.get('attachment')?.markAsDirty();
  }

  save(): void {
    this.loading = true;

    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      this.loading = false;
      return;
    }
    // The form value is untyped, like every reactive form of this module.
    const taskFormValue: any = { ...this.formGroup.value };
    // The one moment control becomes the two stored facts (E4 RF-04, RF-05).
    const scheduled = taskFormValue.moment as ScheduleMoment | null;
    delete taskFormValue.moment;
    taskFormValue.attachments = this.attachments;
    taskFormValue.owner = super.getIDCurrentUser();
    taskFormValue.deadlineAt = scheduled?.dueAt ? new Date(scheduled.dueAt) : null;
    taskFormValue.untimed = scheduled?.untimed === true;

    const saveObservable = this.isNew
      ? this.taskService.create(taskFormValue as Task)
      : this.taskService.update(this.task._id, taskFormValue as Task);

    saveObservable.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success(this.isNew ? this.t('task.form.createdSuccess') : this.t('task.form.updatedSuccess'));
          this.goBack();
        },
        error: (err) => {
          this.alertService.error(err?.error?.message ?? this.t('task.form.saveError'));
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        }
      });
  }

  formatBytes(bytes: number): string {
    const KB = 1024, MB = KB * 1024, GB = MB * 1024;
    if (bytes >= KB) return `${(bytes / KB).toFixed(2)} KB`;
    return `${bytes} B`;
  }
}
